import { showSaveTimeoutRefreshModal } from './refresh-modal';

export function waitUntilProjectSaved(timeoutMs = 1500): Promise<void> {
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        resolve();
      }
    };
    const onSaved = () => {
      try {
        window.removeEventListener('project_saved', onSaved);
      } catch {}
      finish();
    };
    try {
      window.addEventListener('project_saved', onSaved, { once: true });
    } catch {
      // 如果环境不支持事件，直接放行
      finish();
      return;
    }
    // 兜底超时
    setTimeout(() => {
      try { window.removeEventListener('project_saved', onSaved); } catch {}
      finish();
    }, Math.max(0, timeoutMs | 0));
  });
}

export type ProjectSaveWaitResult =
  | { ok: true }
  | { ok: false; reason: 'failed'; error?: unknown }
  | { ok: false; reason: 'timeout' }
  | { ok: false; reason: 'unsupported' };

/**
 * 等待一次“保存结果”（成功或失败），用于在关键操作前严格确保已保存。
 * - 成功：收到 `project_saved`
 * - 失败：收到 `project_save_failed`
 * - 超时：timeoutMs 到期
 */
export function waitUntilProjectSaveResult(timeoutMs = 1500): Promise<ProjectSaveWaitResult> {
  return new Promise<ProjectSaveWaitResult>((resolve) => {
    let done = false;
    const finish = (res: ProjectSaveWaitResult) => {
      if (done) return;
      done = true;
      resolve(res);
    };

    const cleanup = () => {
      try { window.removeEventListener('project_saved', onSaved as any); } catch {}
      try { window.removeEventListener('project_save_failed', onFailed as any); } catch {}
    };

    const onSaved = () => {
      cleanup();
      finish({ ok: true });
    };

    const onFailed = (e: any) => {
      cleanup();
      finish({ ok: false, reason: 'failed', error: e?.detail?.error });
    };

    try {
      window.addEventListener('project_saved', onSaved as any, { once: true });
      window.addEventListener('project_save_failed', onFailed as any, { once: true });
    } catch {
      finish({ ok: false, reason: 'unsupported' });
      return;
    }

    setTimeout(() => {
      cleanup();
      finish({ ok: false, reason: 'timeout' });
    }, Math.max(0, timeoutMs | 0));
  });
}

/**
 * 触发一次“立即保存”，并等待 `project_saved`（最佳努力）。
 *
 * 注意：这里刻意先创建 wait promise 再 dispatch，避免“先触发保存、后挂监听”导致错过事件的竞态。
 */
export async function requestProjectSaveAndWait(timeoutMs = 1500) {
  const wait = waitUntilProjectSaved(timeoutMs);
  try {
    window.dispatchEvent(new Event('save_project_now'));
  } catch {}
  try {
    await wait;
  } catch {}
}

/**
 * 触发一次“立即保存”，并等待“成功或失败”（严格结果）。
 * - 若画布未监听该事件，会超时返回 timeout
 */
export async function requestProjectSaveAndWaitResult(timeoutMs = 1500): Promise<ProjectSaveWaitResult> {
  const wait = waitUntilProjectSaveResult(timeoutMs);
  try {
    window.dispatchEvent(new Event('save_project_now'));
  } catch {}
  return await wait;
}

/**
 * 优先使用 `window.__flowmuseSaveNow()` 触发保存，并等待“成功或失败”（严格结果）。
 * - 若未挂载 saveNow，则回退到事件触发。
 */
export async function forceProjectSaveAndWaitResult(timeoutMs = 1500): Promise<ProjectSaveWaitResult> {
  const wait = waitUntilProjectSaveResult(timeoutMs);
  try {
    const saveFn = (window as any)?.__flowmuseSaveNow;
    if (typeof saveFn === 'function') {
      await saveFn();
    } else {
      try {
        window.dispatchEvent(new Event('save_project_now'));
      } catch {}
    }
  } catch {
    // ignore - failure will be reflected by project_save_failed or timeout
  }
  return await wait;
}

/**
 * 生成前使用：确保画布保存成功；否则中断并提示刷新。
 * - 保存失败：保存逻辑内部会弹“保存失败”刷新弹窗，这里只返回 false。
 * - 超时/未支持：弹“保存超时”刷新弹窗并返回 false。
 */
export async function ensureProjectSavedOrShowRefreshModal(timeoutMs = 10_000): Promise<boolean> {
  const res = await forceProjectSaveAndWaitResult(timeoutMs);
  if (res.ok) return true;
  if (res.reason !== 'failed') {
    try {
      showSaveTimeoutRefreshModal();
    } catch {}
  }
  return false;
}

export async function autoGenerateAfterSave(nodeId: string, timeoutMs = 1500) {
  await requestProjectSaveAndWait(timeoutMs);
  try {
    window.dispatchEvent(new CustomEvent('auto_generate_node', { detail: { id: nodeId } }));
  } catch {}
}


